import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Award, Filter, Search, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import RotatingText from '../components/common/RotatingText';
import CustomSelect from '../components/common/CustomSelect';
import { fetchResults } from '../services/resultsApi';
import { sanitizeInput } from '../utils/inputSanitization';

const ResultsPage = () => {
  const { isDarkMode } = useTheme();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, men, women, juniors, seniors
  const [yearFilter, setYearFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setLoading(true);
      const response = await fetchResults({
        status: 'published'
      });
      
      if (response.success && response.data) {
        setResults(response.data);
        
        // Extract unique years from results
        const years = [...new Set(
          response.data.map(r => new Date(r.tournament_date).getFullYear())
        )].sort((a, b) => b - a);
        
        setAvailableYears(years);
      }
    } catch (err) {
      console.error('Error loading results:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter results
  const filteredResults = results.filter(result => {
    // Category filter - exact match
    if (filter !== 'all' && result.category !== filter) {
      return false;
    }
    
    // Year filter
    if (yearFilter !== 'all') {
      const resultYear = new Date(result.tournament_date).getFullYear();
      if (resultYear !== parseInt(yearFilter)) {
        return false;
      }
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        result.tournament_name?.toLowerCase().includes(search) ||
        result.winner_name?.toLowerCase().includes(search) ||
        result.venue?.toLowerCase().includes(search) ||
        result.report_title?.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  // Group results by year
  const groupedByYear = filteredResults.reduce((groups, result) => {
    const year = new Date(result.tournament_date).getFullYear();
    if (!groups[year]) {
      groups[year] = [];
    }
    groups[year].push(result);
    return groups;
  }, {});

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/resultsHero.png" 
            alt="Tournament Results" 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  Results
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Celebrating</span>
              <RotatingText
                texts={[
                  'Champions',
                  'Excellence',
                  'Victories',
                  'Achievement',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Complete tournament results and winner highlights
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className={`sticky top-16 z-20 border-b ${
        isDarkMode
          ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700'
          : 'bg-fsgu-white/95 border-fsgu-silver-200'
      } backdrop-blur-md`}>
        <div className="container-custom py-4">
          {/* Search & Filters Row */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-4xl mx-auto mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`w-4 h-4 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                }`} />
              </div>
              <input
                type="text"
                placeholder="Search tournaments, winners..."
                value={searchTerm}
                onChange={(e) => {
                  const sanitized = sanitizeInput(e.target.value, 'text');
                  setSearchTerm(sanitized);
                }}
                className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700 text-fsgu-silver-100 placeholder-fsgu-silver-500'
                    : 'bg-fsgu-white border-fsgu-silver-300 text-fsgu-silver-900 placeholder-fsgu-silver-400'
                } focus:outline-none focus:ring-2 focus:ring-fsgu-gold-500`}
              />
            </div>

            {/* Category Filter */}
            <div className="min-w-[180px]">
              <CustomSelect
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Categories' },
                  { value: "Men's Open", label: "Men's Open" },
                  { value: "Men's Mid-Am", label: "Men's Mid-Am" },
                  { value: "Men's Senior", label: "Men's Senior" },
                  { value: "Men's Super Senior", label: "Men's Super Senior" },
                  { value: "Ladies Open", label: "Ladies Open" },
                  { value: "Ladies Mid-Am", label: "Ladies Mid-Am" },
                  { value: "Ladies Senior", label: "Ladies Senior" },
                  { value: "Ladies Super Senior", label: "Ladies Super Senior" },
                  { value: 'Junior Boys', label: 'Junior Boys' },
                  { value: 'Junior Girls', label: 'Junior Girls' },
                  { value: 'Mixed', label: 'Mixed' },
                  { value: 'Country Districts', label: 'Country Districts' }
                ]}
                placeholder="All Categories"
              />
            </div>

            {/* Year Filter */}
            <div className="min-w-[120px]">
              <CustomSelect
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Years' },
                  ...availableYears.map(year => ({
                    value: year.toString(),
                    label: year.toString()
                  }))
                ]}
                placeholder="All Years"
              />
            </div>
          </div>

          {/* Results Count */}
          <div className={`text-sm text-center ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''}
          </div>
        </div>
      </section>

      {/* Results Content */}
      <section className="section">
        <div className="container-custom">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-gold-500 mb-4"></div>
                <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                  Loading results...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className={`p-6 rounded-xl border text-center ${
              isDarkMode
                ? 'bg-fsgu-red-900/20 border-fsgu-red-500/30 text-fsgu-red-200'
                : 'bg-fsgu-red-50 border-fsgu-red-200 text-fsgu-red-700'
            }`}>
              <p>{error}</p>
            </div> 
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-20">
              <Award className={`w-16 h-16 mx-auto mb-4 ${
                isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
              }`} />
              <h3 className={`text-xl font-semibold mb-2 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                Coming Soon
              </h3>
              {/* <p className={`${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
              }`}>
                Try adjusting your filters or search term
              </p> */}
            </div>
          ) : (
            <div className="space-y-12">
              {Object.keys(groupedByYear).sort((a, b) => b - a).map(year => (
                <div key={year}>
                  {/* Year Header */}
                  <div className={`flex items-center gap-4 mb-6 pb-3 border-b ${
                    isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                  }`}>
                    <Calendar className={`w-6 h-6 ${
                      isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
                    }`} />
                    <h2 className={`text-3xl font-bold ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      {year}
                    </h2>
                    <span className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      {groupedByYear[year].length} tournament{groupedByYear[year].length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Results Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {groupedByYear[year].map(result => (
                      <ResultCard key={result.id} result={result} isDarkMode={isDarkMode} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Helper function to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '');
};

// Result Card Component
const ResultCard = ({ result, isDarkMode }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Men's Open": 'blue',
      "Men's Mid-Am": 'blue',
      "Men's Senior": 'purple',
      "Men's Super Senior": 'orange',
      'Junior Boys': 'green',
      "Ladies Open": 'pink',
      "Ladies Mid-Am": 'pink',
      "Ladies Senior": 'purple',
      "Ladies Super Senior": 'orange',
      'Junior Girls': 'green',
      'Mixed': 'gold',
      'Country Districts': 'teal'
    };
    return colors[category] || 'blue';
  };

  const categoryColor = getCategoryColor(result.category);

  return (
    <Link
      to={`/results/${result.slug || result.id}`}
      className={`block group rounded-xl overflow-hidden border transition-all duration-300 ${
        isDarkMode
          ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-gold-500/50 hover:shadow-gold-glow'
          : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-gold-500/50 hover:shadow-soft-lg'
      }`}
    >
      {/* Image */}
      {result.featured_image_url && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={result.featured_image_url}
            alt={result.tournament_name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Category Badge */}
          <div className="absolute top-3 right-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium bg-fsgu-${categoryColor}-500 text-white`}>
              {result.category}
            </span>
          </div>

          {/* Date Badge */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg">
            <Calendar className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">
              {formatDate(result.tournament_date)}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        <h3 className={`text-lg font-bold mb-2 group-hover:text-fsgu-gold-500 transition-colors ${
          isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
        }`}>
          {result.tournament_name || result.report_title}
        </h3>

        {/* Winner Info */}
        {result.winner_name && (
          <div className="flex items-center gap-2 mb-3">
            <Award className={`w-4 h-4 ${
              isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
            }`} />
            <div className="flex-1">
              <div className={`text-sm font-semibold ${
                isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
              }`}>
                {result.winner_name}
              </div>
              {result.winner_score && (
                <div className={`text-xs ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  Score: {result.winner_score}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Venue */}
        {result.venue && (
          <div className={`flex items-center gap-2 text-sm mb-3 ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            <MapPin className="w-4 h-4" />
            <span>{result.venue}</span>
          </div>
        )}

        {/* Report Excerpt - Strip HTML tags for preview */}
        {result.report_content && (
          <p className={`text-sm line-clamp-2 mb-3 ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            {stripHtml(result.report_content)}
          </p>
        )}

        {/* Read More */}
        <div className={`flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all ${
          isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-600'
        }`}>
          <span>Read Full Report</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
};

export default ResultsPage;